/**
 * EditableSettingsRow — inline-edit variant of SettingsRow.
 *
 * Renders like a normal iPad Settings row. On tap, the value side
 * transforms to an input (text, number, toggle, or dimensions).
 * On blur / Enter the input closes and the zustand store is updated.
 *
 * Used only for TikTok Listing detail page editing.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Switch } from "@/components/ui/switch";
import {
  useTikTokEditStore,
  selectFieldValue,
  selectIsFieldDirty,
  selectIsReadOnly,
} from "../stores/useTikTokEditStore";
import { parseJsonField } from "./shared";

// ── Types ──────────────────────────────────────────────────────────

export type EditType = "text" | "number" | "toggle" | "readonly" | "dimensions";

interface EditableSettingsRowProps {
  label: string;
  /** Display value (from formData, used as fallback) */
  value: any;
  /** Key in the dirty store (e.g., "title", "video_url") */
  fieldKey: string;
  editType: EditType;
  maxLength?: number;
  minLength?: number;
  suffix?: string;
  placeholder?: string;
  /** Custom display renderer for readonly mode (e.g., external link) */
  displayValue?: React.ReactNode;
}

// ── Component ──────────────────────────────────────────────────────

export function EditableSettingsRow({
  label,
  value,
  fieldKey,
  editType,
  maxLength,
  minLength,
  suffix,
  placeholder,
  displayValue,
}: EditableSettingsRowProps) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const storeValue = useTikTokEditStore(selectFieldValue(fieldKey));
  const isDirty = useTikTokEditStore(selectIsFieldDirty(fieldKey));
  const setField = useTikTokEditStore((s) => s.setField);
  const isReadOnly = useTikTokEditStore(selectIsReadOnly);

  // The current value is dirty value if exists, else the formData value
  const currentValue = storeValue ?? value;

  // Auto-focus when editing starts
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select all text for easy replacement
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  const commitValue = useCallback(
    (newValue: any) => {
      setField(fieldKey, newValue);
      setEditing(false);
    },
    [fieldKey, setField],
  );

  // ── Global read-only (frozen / platform-deactivated) ─────────

  if (isReadOnly && editType !== "readonly") {
    return (
      <div className="flex items-center gap-4 px-4 py-5 opacity-60">
        <span
          className="text-lg w-1/2"
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          {label}
        </span>
        <div className="text-lg w-1/2 text-right text-foreground">
          <span className="break-words whitespace-pre-wrap">
            {displayValue ?? currentValue ?? "\u2014"}
            {suffix && currentValue != null ? ` ${suffix}` : ""}
          </span>
        </div>
      </div>
    );
  }

  // ── Readonly ──────────────────────────────────────────────────

  if (editType === "readonly") {
    return (
      <div className="flex items-center gap-4 px-4 py-5">
        <span
          className="text-lg w-1/2"
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          {label}
        </span>
        <div className="text-lg w-1/2 text-right text-foreground">
          <span className="break-words whitespace-pre-wrap">
            {displayValue ?? currentValue ?? "\u2014"}
          </span>
        </div>
      </div>
    );
  }

  // ── Toggle ────────────────────────────────────────────────────

  if (editType === "toggle") {
    const checked = Boolean(currentValue);
    return (
      <div className="flex items-center gap-4 px-4 py-5">
        <span
          className="text-lg w-1/2"
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          {label}
        </span>
        <div className="w-1/2 flex justify-end">
          <Switch
            checked={checked}
            onCheckedChange={(val) => setField(fieldKey, val ? 1 : 0)}
          />
        </div>
      </div>
    );
  }

  // ── Dimensions (L × W × H) ───────────────────────────────────

  if (editType === "dimensions") {
    return (
      <DimensionsRow
        label={label}
        fieldKey={fieldKey}
        value={currentValue}
        isDirty={isDirty}
        suffix={suffix}
      />
    );
  }

  // ── Text / Number (inline input) ──────────────────────────────

  if (editing) {
    return (
      <div className="flex flex-col px-4 py-3">
        <span
          className="text-lg mb-1"
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          {label}
        </span>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={editType === "number" ? "number" : "text"}
          className="w-full rounded-sm border border-border bg-background px-3 py-2 text-lg outline-none focus:ring-2 focus:ring-primary/40"
          defaultValue={currentValue ?? ""}
          placeholder={placeholder}
          maxLength={maxLength}
          onBlur={(e) => {
            const raw = e.target.value;
            const val = editType === "number" ? parseFloat(raw) || 0 : raw;
            commitValue(val);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "Escape") {
              setEditing(false);
            }
          }}
        />
        {maxLength != null && (
          <CharCounter
            current={String(currentValue ?? "").length}
            max={maxLength}
            min={minLength}
          />
        )}
      </div>
    );
  }

  // ── Display mode (tappable) ───────────────────────────────────

  const display = displayValue ?? (
    <>
      {currentValue ?? "\u2014"}
      {suffix && currentValue != null ? ` ${suffix}` : ""}
    </>
  );

  return (
    <button
      type="button"
      className="flex items-center gap-4 px-4 py-5 w-full text-left hover:bg-accent/50 transition-colors"
      onClick={() => setEditing(true)}
    >
      <span
        className="text-lg w-1/2"
        style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
      >
        {label}
      </span>
      <div className="text-lg w-1/2 text-right text-foreground">
        <span
          className={`break-words whitespace-pre-wrap ${isDirty ? "font-medium" : ""}`}
        >
          {display || (
            <span className="text-muted-foreground">{placeholder ?? "\u2014"}</span>
          )}
        </span>
      </div>
    </button>
  );
}

// ── CharCounter sub-component ─────────────────────────────────────

function CharCounter({
  current,
  max,
  min,
}: {
  current: number;
  max: number;
  min?: number;
}) {
  const isOverMax = current > max;
  const isUnderMin = min != null && current > 0 && current < min;
  const color = isOverMax || isUnderMin ? "text-red-500" : "text-muted-foreground";

  return (
    <span className={`text-xs mt-1 text-right ${color}`}>
      {current}/{max}
    </span>
  );
}

// ── DimensionsRow sub-component ───────────────────────────────────

function DimensionsRow({
  label,
  fieldKey,
  value,
  isDirty,
  suffix,
}: {
  label: string;
  fieldKey: string;
  value: any;
  isDirty: boolean;
  suffix?: string;
}) {
  const setField = useTikTokEditStore((s) => s.setField);
  const [editing, setEditing] = useState(false);

  // Parse dimensions JSON
  const dims = parseDimensions(value);

  const commitDimensions = useCallback(
    (l: number, w: number, h: number) => {
      setField(fieldKey, {
        length: l,
        width: w,
        height: h,
        unit: "cm",
      });
      setEditing(false);
    },
    [fieldKey, setField],
  );

  if (editing) {
    return (
      <DimensionsEditor
        label={label}
        initial={dims}
        onCommit={commitDimensions}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const display =
    dims.length || dims.width || dims.height
      ? `${dims.length} × ${dims.width} × ${dims.height} ${suffix ?? "cm"}`
      : null;

  return (
    <button
      type="button"
      className="flex items-center gap-4 px-4 py-5 w-full text-left hover:bg-accent/50 transition-colors"
      onClick={() => setEditing(true)}
    >
      <span
        className="text-lg w-1/2"
        style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
      >
        {label}
      </span>
      <div className="text-lg w-1/2 text-right text-foreground">
        <span className={`break-words ${isDirty ? "font-medium" : ""}`}>
          {display ?? "\u2014"}
        </span>
      </div>
    </button>
  );
}

// ── DimensionsEditor ──────────────────────────────────────────────

function DimensionsEditor({
  label,
  initial,
  onCommit,
  onCancel,
}: {
  label: string;
  initial: { length: number; width: number; height: number };
  onCommit: (l: number, w: number, h: number) => void;
  onCancel: () => void;
}) {
  const lRef = useRef<HTMLInputElement>(null);
  const wRef = useRef<HTMLInputElement>(null);
  const hRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    lRef.current?.focus();
    lRef.current?.select();
  }, []);

  const commit = () => {
    onCommit(
      parseFloat(lRef.current?.value ?? "0") || 0,
      parseFloat(wRef.current?.value ?? "0") || 0,
      parseFloat(hRef.current?.value ?? "0") || 0,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    }
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className="flex flex-col px-4 py-3">
      <span
        className="text-lg mb-2"
        style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
      >
        {label}
      </span>
      <div className="flex items-center gap-2">
        <input
          ref={lRef}
          type="number"
          className="w-20 rounded-sm border border-border bg-background px-2 py-1.5 text-center text-lg outline-none focus:ring-2 focus:ring-primary/40"
          defaultValue={initial.length || ""}
          placeholder="L"
          onKeyDown={handleKeyDown}
          onBlur={commit}
        />
        <span className="text-muted-foreground">×</span>
        <input
          ref={wRef}
          type="number"
          className="w-20 rounded-sm border border-border bg-background px-2 py-1.5 text-center text-lg outline-none focus:ring-2 focus:ring-primary/40"
          defaultValue={initial.width || ""}
          placeholder="W"
          onKeyDown={handleKeyDown}
        />
        <span className="text-muted-foreground">×</span>
        <input
          ref={hRef}
          type="number"
          className="w-20 rounded-sm border border-border bg-background px-2 py-1.5 text-center text-lg outline-none focus:ring-2 focus:ring-primary/40"
          defaultValue={initial.height || ""}
          placeholder="H"
          onKeyDown={handleKeyDown}
        />
        <span className="text-sm text-muted-foreground ml-1">cm</span>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function parseDimensions(value: any): {
  length: number;
  width: number;
  height: number;
} {
  if (!value) return { length: 0, width: 0, height: 0 };
  const parsed = typeof value === "string" ? parseJsonField<any>(value) : value;
  if (!parsed || typeof parsed !== "object") return { length: 0, width: 0, height: 0 };
  return {
    length: parseFloat(parsed.length) || 0,
    width: parseFloat(parsed.width) || 0,
    height: parseFloat(parsed.height) || 0,
  };
}
